import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  SensorDescriptor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { ApptSegment, SegmentedAppointment } from '../segments';
import { TechLane, resolveTechColor } from '../useSchedulingData';
import { ScheduleException } from '../../../services/scheduleApi';
import ApptBlock from '../parts/ApptBlock';
import ExceptionBlock from '../parts/ExceptionBlock';
import { minutesOfDay, minutesToHeight, minutesToTop, pointToSlot, snapMinutesTo } from '../parts/laneMath';
import { bucketExceptionsByDate, PositionedException, slotStartLocal } from '../exceptionSegments';
import { DropIntent } from './WeekGrid';

// SP-1 Task 9: exported so MobileDay can target the Unassigned lane via
// `onlyLaneKey` without duplicating the string literal.
export const UNASSIGNED_LANE_KEY = 'unassigned';
// The single fallback lane key used when no `techs` are provided at all —
// exported for the same reason as UNASSIGNED_LANE_KEY above.
export const ALL_LANE_KEY = 'all';

// Same shop window as WeekGrid — kept local (not shared) since each grid's
// track height differs; the *math* (laneMath) is what's shared, not the constants.
const OPEN_MIN = 8 * 60;
const CLOSE_MIN = 18 * 60;
const TRACK_HEIGHT = 720;
const MIN_BLOCK_HEIGHT = 18;

export interface DayGridProps {
  date: Dayjs;
  appts: SegmentedAppointment[];
  techs: TechLane[];
  exceptions: ScheduleException[];
  onOpen: (id: number) => void;
  onDrop?: (e: DropIntent) => void;
  sensors?: SensorDescriptor<any>[];
  /** Tap an existing availability block → edit it (gated by the caller). */
  onEditBlock?: (ex: ScheduleException) => void;
  /** Tap an empty slot in a lane → create a block prefilled with that lane's
   *  tech + the tapped 15-min start. */
  onCreateBlock?: (techId: number | undefined, startLocal: string) => void;
  /** SP-1 Task 9: when set, restricts rendering to the single lane whose key
   *  matches (a tech's `String(id)`, or `UNASSIGNED_LANE_KEY`) — used by
   *  MobileDay to show one full-width tech column instead of all lanes
   *  side-by-side. Bucketing and color resolution still run against the full
   *  `techs` list passed in, so classification (and the Unassigned fallback
   *  for unknown techs) is identical to the unfiltered grid — only the final
   *  rendered lane list is narrowed. Leave unset for the existing
   *  all-lanes behavior (desktop Day, MobileWeek). */
  onlyLaneKey?: string;
}

interface DaySegment {
  appt: SegmentedAppointment;
  segment: ApptSegment;
}

interface Lane {
  key: string;
  techId?: number;
  name: string;
}

interface DragData {
  appointmentId: number;
  date: string;
  startMinutes: number;
  technicianId?: number;
}

interface DropData {
  date: string;
  technicianId?: number;
}

interface DraggableSegmentProps {
  appt: SegmentedAppointment;
  segment: ApptSegment;
  technicianId?: number;
  techColor?: string;
  techInitials?: string;
  onOpen: (id: number) => void;
}

function DraggableSegment({ appt, segment, technicianId, techColor, techInitials, onOpen }: DraggableSegmentProps) {
  const startMin = minutesOfDay(dayjs(segment.start));
  const rawEndMin = minutesOfDay(dayjs(segment.end));
  const endMin = rawEndMin > startMin ? rawEndMin : CLOSE_MIN;
  // Only the segment that begins the appointment can be dragged — dragging a
  // continuation slice (segIndex > 0) would only move part of a multi-day job.
  const canDrag = segment.segIndex === 0;

  const dragData: DragData = {
    appointmentId: appt.id!,
    date: segment.date,
    startMinutes: startMin,
    technicianId,
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `appt-${appt.id}-${segment.segIndex}`,
    data: dragData,
    disabled: !canDrag,
  });

  return (
    <div
      ref={setNodeRef}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
      style={{
        position: 'absolute',
        top: minutesToTop(startMin, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT),
        left: 2,
        right: 2,
        height: minutesToHeight(startMin, endMin, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT, MIN_BLOCK_HEIGHT),
        opacity: isDragging ? 0.5 : 1,
        cursor: canDrag ? 'grab' : undefined,
      }}
    >
      <ApptBlock appt={appt} segment={segment} onOpen={onOpen} draggable={canDrag} techColor={techColor} techInitials={techInitials} />
    </div>
  );
}

interface LaneColumnProps {
  laneKey: string;
  techId?: number;
  name: string;
  date: string;
  segments: DaySegment[];
  laneExceptions: PositionedException[];
  onOpen: (id: number) => void;
  onEditBlock?: (ex: ScheduleException) => void;
  onCreateBlock?: (techId: number | undefined, startLocal: string) => void;
  hourMarks: number[];
  techs: TechLane[];
}

function LaneColumn({ laneKey, techId, name, date, segments, laneExceptions, onOpen, onEditBlock, onCreateBlock, hourMarks, techs }: LaneColumnProps) {
  const dropData: DropData = { date, technicianId: techId };
  const { setNodeRef, isOver } = useDroppable({
    id: `lane-${laneKey}`,
    data: dropData,
  });

  // Empty-slot tap → create a block in this lane's tech at the tapped 15-min
  // start. Only fires on a click directly on the track (blocks stop their own
  // clicks) and only when a create handler is wired.
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onCreateBlock) return;
    if (e.target !== e.currentTarget) return;
    const startMin = pointToSlot(e.nativeEvent.offsetY, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT);
    onCreateBlock(techId, slotStartLocal(date, startMin));
  };

  return (
    <div
      key={laneKey}
      data-testid={`day-lane-${laneKey}`}
      style={{ flex: '1 0 160px', minWidth: 160, borderLeft: '1px solid #f0f0f0' }}
    >
      <div
        style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: 13,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {name}
      </div>
      <div
        ref={setNodeRef}
        data-testid={`day-track-${laneKey}`}
        onClick={handleTrackClick}
        style={{
          position: 'relative',
          height: TRACK_HEIGHT,
          backgroundColor: isOver ? 'rgba(37,99,235,0.04)' : undefined,
          transition: 'background-color 0.1s',
          cursor: onCreateBlock ? 'copy' : undefined,
        }}
      >
        {hourMarks.map((h) => (
          <div
            key={h}
            style={{
              position: 'absolute',
              top: minutesToTop(h * 60, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT),
              left: 0,
              right: 0,
              borderTop: '1px solid #f5f5f5',
              pointerEvents: 'none',
            }}
          />
        ))}
        {/* Availability blocks render behind appointments (lower z-index). */}
        {laneExceptions.map((pos, i) => (
          <ExceptionBlock
            key={`ex-${pos.exception.id ?? 'x'}-${i}`}
            exception={pos.exception}
            top={pos.top}
            height={pos.height}
            onEdit={() => onEditBlock?.(pos.exception)}
          />
        ))}
        {segments.map(({ appt, segment }) => (
          <DraggableSegment
            key={`${segment.appointmentId}-${segment.segIndex}`}
            appt={appt}
            segment={segment}
            technicianId={techId}
            techColor={resolveTechColor(techs, appt.technician_id)}
            onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  );
}

export function DayGrid({ date, appts, techs, exceptions, onOpen, onDrop, sensors, onEditBlock, onCreateBlock, onlyLaneKey }: DayGridProps) {
  const dateStr = date.format('YYYY-MM-DD');

  // Expand + position exceptions for just this day, then bucket per lane by the
  // owning user_id. Shop-wide holidays are already fanned out into one row per
  // tech (each carrying its own user_id + a shared group_id), so they land in
  // every tech lane here automatically. An exception whose user_id doesn't
  // match any active-tech lane (inactive tech, owner/advisor, or some other
  // non-tech row) must still render somewhere — bucket it into the same
  // catch-all Unassigned lane the appointment fallback below uses, instead of
  // silently dropping it.
  const knownTechIdsForExceptions = new Set(techs.map((t) => String(t.id)));
  const positionedForDay = bucketExceptionsByDate(
    exceptions || [],
    dateStr,
    dateStr,
    OPEN_MIN,
    CLOSE_MIN,
    TRACK_HEIGHT,
  ).get(dateStr) || [];
  const exceptionsByLane = new Map<string, PositionedException[]>();
  for (const pos of positionedForDay) {
    let laneKey: string;
    if (!techs.length) {
      laneKey = 'all';
    } else if (knownTechIdsForExceptions.has(String(pos.exception.user_id))) {
      laneKey = String(pos.exception.user_id);
    } else {
      laneKey = UNASSIGNED_LANE_KEY;
    }
    const list = exceptionsByLane.get(laneKey) || [];
    list.push(pos);
    exceptionsByLane.set(laneKey, list);
  }

  const fallbackSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );
  const activeSensors = sensors ?? fallbackSensors;

  // Appts assigned to nobody, or to someone outside the active-tech list
  // (inactive tech, or an owner — owners are assignable), must still render
  // somewhere: bucket anything that doesn't match a known lane into a
  // catch-all "Unassigned" lane instead of silently dropping it.
  const knownTechIds = new Set(techs.map((t) => String(t.id)));
  const lanes: Lane[] = techs.length
    ? techs
        .map((t): Lane => ({ key: String(t.id), techId: t.id, name: t.name }))
        .concat([{ key: UNASSIGNED_LANE_KEY, techId: undefined, name: 'Unassigned' }])
    : [{ key: 'all', techId: undefined, name: 'All' }];

  const segmentsByLane = new Map<string, DaySegment[]>();
  for (const appt of appts) {
    for (const segment of appt.segments) {
      if (segment.date !== dateStr) continue;
      let laneKey: string;
      if (!techs.length) {
        laneKey = 'all';
      } else if (appt.technician_id != null && knownTechIds.has(String(appt.technician_id))) {
        laneKey = String(appt.technician_id);
      } else {
        laneKey = UNASSIGNED_LANE_KEY;
      }
      const list = segmentsByLane.get(laneKey) || [];
      list.push({ appt, segment });
      segmentsByLane.set(laneKey, list);
    }
  }

  const hourMarks = Array.from(
    { length: (CLOSE_MIN - OPEN_MIN) / 60 + 1 },
    (_, i) => OPEN_MIN / 60 + i,
  );

  // Narrow to a single lane for mobile Day (Task 9) without touching the
  // bucketing above — segmentsByLane/exceptionsByLane already classified
  // everything against the full `lanes` list, so this only trims what
  // renders, it doesn't change which lane an appt/exception belongs to.
  const visibleLanes = onlyLaneKey ? lanes.filter((l) => l.key === onlyLaneKey) : lanes;

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const dragData = active.data.current as DragData | undefined;
    const overData = over.data.current as DropData | undefined;
    if (!dragData || !overData) return;

    const activeRect = active.rect.current.translated;
    const overRect = (over as any).rect;
    let startMinutes = dragData.startMinutes;
    if (activeRect && overRect && typeof overRect.top === 'number') {
      const offsetY = activeRect.top - overRect.top;
      startMinutes = pointToSlot(offsetY, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT);
    }

    const targetTechId = overData.technicianId;
    const techChanged = targetTechId !== undefined && targetTechId !== dragData.technicianId;
    // Compare the pixel-derived (snapped) drop position against the appt's
    // own origin start snapped to the same resolution — a purely lateral
    // (tech-lane) drag can shift the raw pixel offset by a few px even when
    // the user never touched the vertical axis, and comparing raw-vs-snapped
    // would misreport that noise as an intentional time change (and force a
    // 15-min snap onto an appt that wasn't on the grid to begin with).
    const startChanged = overData.date !== dragData.date || startMinutes !== snapMinutesTo(dragData.startMinutes);
    const noOp = !startChanged && !techChanged;
    if (noOp) return;

    onDrop?.({
      appointmentId: dragData.appointmentId,
      date: overData.date,
      startMinutes,
      startChanged,
      ...(techChanged ? { technicianId: targetTechId } : {}),
    });
  }

  return (
    <DndContext sensors={activeSensors} onDragEnd={handleDragEnd} autoScroll>
      <div style={{ overflowX: 'auto', width: '100%' }} data-testid="day-grid">
        <div style={{ display: 'flex', minWidth: visibleLanes.length * 160 + 56 }}>
          <div style={{ width: 56, flexShrink: 0 }}>
            <div style={{ height: 40 }} />
            <div style={{ position: 'relative', height: TRACK_HEIGHT }}>
              {hourMarks.map((h) => (
                <div
                  key={h}
                  style={{
                    position: 'absolute',
                    top: minutesToTop(h * 60, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT) - 6,
                    right: 8,
                    fontSize: 11,
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  {dayjs().hour(h).minute(0).format('h A')}
                </div>
              ))}
            </div>
          </div>

          {visibleLanes.map((lane) => (
            <LaneColumn
              key={lane.key}
              laneKey={lane.key}
              techId={lane.techId}
              name={lane.name}
              date={dateStr}
              segments={segmentsByLane.get(lane.key) || []}
              laneExceptions={exceptionsByLane.get(lane.key) || []}
              onOpen={onOpen}
              onEditBlock={onEditBlock}
              onCreateBlock={onCreateBlock}
              hourMarks={hourMarks}
              techs={techs}
            />
          ))}
        </div>
      </div>
    </DndContext>
  );
}

export default DayGrid;
